import React from 'react'
import { useAudioMarkerEngine } from './useAudioMarkerEngine'
import AudioStemRow from '@/features/services/media/components/AudioStemRow'
import UiSlider from '../../../../services/media/components/ui/UiSlider'

type AudioEngine = ReturnType<typeof useAudioMarkerEngine>

interface StemRowsPanelProps {
  engine: AudioEngine
}

/**
 * StemRowsPanel - Renders audio tracks/stems using AudioStemRow from Music Production
 * 
 * Reuses the same UI pattern as AudioLibraryPlayer for consistency across the app.
 * Displays master level + individual track controls (volume, mute, solo, meters).
 */
export default function StemRowsPanel({ engine }: StemRowsPanelProps) {
  if (engine.tracks.length === 0) {
    return null
  }

  return (
    <div className="shrink-0 space-y-3">
      {/* Master level control */}
      <div className="flex items-center gap-2">
        <span className="text-xs w-20 shrink-0 text-neutral-300">Master</span>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="relative h-2 flex-1 min-w-0 md:w-24 md:flex-none rounded bg-neutral-800 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-purple-500 to-blue-500" 
              style={{ width: `${engine.volume * 100}%` }} 
            />
          </div>
          <span className="text-[10px] w-12 text-right text-neutral-400 cursor-pointer hover:text-neutral-200"
            title="Reset volume to 100%"
            onClick={() => engine.setVolume(1)}
          >
            {Math.round(engine.volume * 100)}%
          </span>
        </div>
      </div>

      {/* Individual track rows */}
      <div className="space-y-2 border-t border-neutral-700/40 pt-3">
        {engine.tracks.map((track) => (
          <AudioStemRow
            key={track.role}
            role={track.role}
            volume={engine.volumesByRole[track.role] ?? 1}
            muted={engine.mutedByRole[track.role] ?? false}
            solo={engine.soloByRole[track.role] ?? false}
            // Level meters (would need analyser node per track to implement fully)
            level={0}
            levelPre={0}
            levelDbfs={undefined}
            attenuationDb={undefined}
            onVolumeChange={(v) => engine.setTrackVolume(track.role, v)}
            onToggleMute={() => engine.toggleTrackMute(track.role)}
            onToggleSolo={() => engine.toggleTrackSolo(track.role)}
          />
        ))}
      </div>
    </div>
  )
}
